/**
 * LiveWidgetSection — self-contained CMS editor for the hero live-snapshot
 * widget. Owns its own fetch, dirty tracking, save & discard flow — writes
 * to /api/live-widget/config, decoupled from the main CMS document.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FieldFull, Hint, Code } from "./primitives";

const WIDGET_FIELDS = [
  { k: "litres_today",     l: "Litres today",           hint: "Sum of litres delivered since 00:00" },
  { k: "bowsers",          l: "Bowsers",                hint: "Active bowser count" },
  { k: "cities",           l: "Cities covered",         hint: "Distinct city count" },
  { k: "deliveries_today", l: "Deliveries today",       hint: "Completed deliveries since 00:00" },
  { k: "monthly_litres",   l: "Monthly litres (badge)", hint: "Shown on the corner 'Delivered' badge" },
];

export function LiveWidgetSection() {
  const qc = useQueryClient();
  const { data: server } = useQuery({
    queryKey: ["live-widget-config"],
    queryFn: () => api.get("/live-widget/config").then((r) => r.data),
  });
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (server) setDraft(structuredClone(server));
  }, [server]);

  const save = useMutation({
    mutationFn: (payload) => api.patch("/live-widget/config", payload).then((r) => r.data),
    onSuccess: (data) => {
      toast.success("Live widget updated");
      qc.setQueryData(["live-widget-config"], data);
      qc.invalidateQueries({ queryKey: ["public-live-stats"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  if (!draft) return <div className="text-xs text-muted-foreground">Loading widget config…</div>;

  const isDirty = JSON.stringify(draft) !== JSON.stringify(server);
  const source = draft.source || "live";
  const overrides = Array.isArray(draft.overrides) ? draft.overrides : [];
  const manual = draft.manual || {};

  const setSource = (v) => setDraft({ ...draft, source: v });
  const toggleOverride = (k) => setDraft({
    ...draft,
    overrides: overrides.includes(k) ? overrides.filter((x) => x !== k) : [...overrides, k],
  });
  const setManualVal = (k, v) => setDraft({ ...draft, manual: { ...manual, [k]: v } });
  const isManualField = (k) => source === "manual" || overrides.includes(k);

  return (
    <>
      <Hint>
        The hero shows a live-snapshot card with four counters plus a corner
        "Delivered" badge. Choose <Code>Live from DB</Code> to pull real numbers
        from delivered orders (auto-refresh every 15 s), or <Code>Manual</Code>
        to enter your own values. Per-field <Code>Manual</Code> pills let you
        mix live and hand-entered values.
      </Hint>
      <FieldFull label="Data source">
        <div className="flex flex-wrap gap-2" data-testid="cms-widget-source-picker">
          {[
            { v: "live",   label: "Live from DB",  hint: "Pulls real numbers · auto-refresh 15 s" },
            { v: "manual", label: "Manual values", hint: "Show only the numbers below" },
          ].map((opt) => {
            const active = source === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => setSource(opt.v)}
                className={`flex-1 min-w-[180px] rounded-lg border p-3 text-left transition-all ${active ? "border-primary bg-primary/10 shadow" : "border-border hover:border-primary/40"}`}
                data-testid={`cms-widget-source-${opt.v}`}
              >
                <div className="text-sm font-bold">{opt.label}</div>
                <div className="text-[10px] text-muted-foreground">{opt.hint}</div>
              </button>
            );
          })}
        </div>
      </FieldFull>
      <FieldFull label={source === "manual" ? "Manual values" : "Manual values (per-field override applies when the pill is Manual)"}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {WIDGET_FIELDS.map((f) => {
            const overridden = overrides.includes(f.k);
            return (
              <div key={f.k} className="rounded-lg border p-3">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <label className="text-xs font-semibold text-muted-foreground">{f.l}</label>
                  {source === "live" && (
                    <button
                      type="button"
                      onClick={() => toggleOverride(f.k)}
                      className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border transition-colors ${overridden ? "bg-primary text-white border-primary" : "text-muted-foreground border-border hover:border-primary/40"}`}
                      data-testid={`cms-widget-override-${f.k}`}
                    >
                      {overridden ? "Manual" : "Live"}
                    </button>
                  )}
                </div>
                <Input
                  type="number"
                  value={manual[f.k] ?? ""}
                  onChange={(e) => setManualVal(f.k, e.target.value === "" ? 0 : Number(e.target.value))}
                  disabled={!isManualField(f.k)}
                  data-testid={`cms-widget-manual-${f.k}`}
                />
                <div className="mt-1 text-[10px] text-muted-foreground">{f.hint}</div>
              </div>
            );
          })}
        </div>
      </FieldFull>
      <div className="mt-4 flex items-center justify-end gap-2">
        {isDirty && (
          <button
            type="button"
            onClick={() => setDraft(structuredClone(server))}
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
            data-testid="cms-widget-discard"
          >
            Discard
          </button>
        )}
        <Button
          size="sm"
          onClick={() => save.mutate(draft)}
          disabled={!isDirty || save.isPending}
          data-testid="cms-widget-save"
        >
          {save.isPending ? "Saving…" : isDirty ? "Save widget" : "Saved"}
        </Button>
      </div>
    </>
  );
}
